/**
 * Cross-tab coordination via BroadcastChannel.
 * - 'signout'                 — tab A signs out → all other tabs reset auth + route to /welcome
 * - 'signin'                  — tab A signs in → other tabs refetch /users/me
 * - 'subscription:invalidate' — premium state changed (post-checkout, portal cancel) →
 *                                other tabs invalidate the subscription query
 */

export type AuthBroadcastEvent =
  | { type: 'signout' }
  | { type: 'signin' }
  | { type: 'subscription:invalidate' };

const CHANNEL_NAME = 'bz_auth';

let channel: BroadcastChannel | null = null;

function ensure(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
}

export function broadcast(event: AuthBroadcastEvent): void {
  const ch = ensure();
  if (!ch) return;
  try {
    ch.postMessage(event);
  } catch {
    // BroadcastChannel.postMessage throws if the structured-clone fails;
    // the events above are simple objects so this should never fire — but
    // we swallow rather than crash the caller.
  }
}

export function subscribeBroadcast(
  handler: (event: AuthBroadcastEvent) => void,
): () => void {
  const ch = ensure();
  if (!ch) return () => undefined;
  const listener = (e: MessageEvent<AuthBroadcastEvent>) => handler(e.data);
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}
